import type { GrupoDeNavegacao, ItemDeNavegacao, PassoDoCaminho } from "@plantus/ui";

import { ROTAS } from "./rotas";
import { SETORES } from "./setores";

/**
 * O caminho até a tela — <b>derivado do mapa de navegação</b>, com a folha
 * declarada pela tela.
 *
 * <h2>A pergunta que este arquivo responde</h2>
 *
 * <p>Derivar da rota ou declarar em cada tela? As duas têm um defeito, e eles
 * são opostos: <b>derivado não mente, mas não sabe o nome do lote</b> — a rota
 * carrega `:id`, que é um identificador, e não `MP-COP-382684`, que é o que está
 * impresso no tambor. <b>Declarado sabe, e pode divergir</b>.
 *
 * <p>E divergir não é hipótese. O que existe hoje é declarado à mão em seis
 * telas, num `&lt;p class="tela__chapeu"&gt;`, e <b>já divergiu em duas</b>:
 *
 * <ul>
 *   <li>`PaginaFornecedores` escreve "Assuntos Regulatórios" e para aí — o
 *       módulo, que é onde a pessoa está, não aparece;</li>
 *   <li>`PaginaPainel` escreve "Painel de risco" sem o setor — o inverso.</li>
 * </ul>
 *
 * <p>Ninguém errou: são seis strings escritas em seis arquivos, e o mapa de
 * navegação mudou depois de algumas delas. É o defeito previsível de dado
 * copiado, e ele não se resolve com revisão de código.
 *
 * <h2>A divisão, então, segue o que cada fonte SABE</h2>
 *
 * <table>
 *   <tr><td><b>Setor e módulo</b></td>
 *       <td>DERIVADOS de {@link SETORES} — a mesma lista que desenha a lateral.
 *           Não podem divergir do menu porque <b>são</b> o menu.</td></tr>
 *   <tr><td><b>A folha</b></td>
 *       <td>DECLARADA pela tela, porque só ela conhece o código do lote. É o
 *           degrau ATUAL, e degrau atual não é link — uma folha errada não leva
 *           ninguém a lugar nenhum.</td></tr>
 * </table>
 *
 * <p>Ou seja: o que pode divergir é derivado; o que não dá para derivar é o
 * único declarado, e é justamente o degrau que não tem para onde levar.
 *
 * <h2>⚠️ Rota fora do mapa NÃO tem caminho, e isso é decisão</h2>
 *
 * <p>`/producao/materias-primas/volume/:token` e
 * `/producao/materias-primas/lote/:codigo` são as telas que o <b>QR do tambor
 * abre</b>. Elas não estão no menu, e por isso a derivação devolve lista vazia
 * — o componente não desenha nada.
 *
 * <p>Isso é o que a feature 1.2.3 pede, não um efeito colateral: quem chega ali
 * escaneou um adesivo no corredor, com o celular na mão diante do tambor, e
 * aquela tela é <b>de consulta e só</b> (R1 e R10). Um caminho ali seria um
 * convite a entrar no sistema a partir de um endereço público impresso — o
 * mesmo motivo pelo qual "o caminho para a etiqueta não volta para cá".
 */

/** Um candidato do mapa: a rota que casou e os degraus até ela. */
interface Casamento {
  rota: string;
  passos: PassoDoCaminho[];
}

/**
 * 🔴 As telas que <b>nunca</b> têm caminho — e a lista vem de {@link ROTAS}.
 *
 * <p>São os dois endereços que o <b>QR impresso no tambor</b> abre. Elas caem
 * embaixo de `/producao/materias-primas` na URL, e por isso o casamento por
 * prefixo as alcançaria: bastaria a tela declarar uma folha para a ficha do
 * corredor ganhar uma trilha inteira de volta ao sistema — inclusive ao módulo
 * que dá acesso à <b>emissão de etiqueta</b>. É exatamente o que a feature 1.2.3
 * proíbe nas suas R1 e R10: *"o caminho para a etiqueta não volta para cá — um
 * botão de imprimir a um toque de quem escaneou um tambor é como uma segunda
 * etiqueta do mesmo lote passa a circular sem ninguém pedir"*.
 *
 * <p>⚠️ A exceção é <b>declarada</b>, e tem de ser: ela não se deduz da forma do
 * endereço. Mas ela é declarada <b>apontando para a constante que o roteador
 * registra</b>, e não copiando o texto da rota — no dia em que aquele endereço
 * mudar (é o que o resolvedor `/qr/&lt;token&gt;` compra), esta recusa muda
 * junto, em vez de virar uma string que protege um endereço que não existe mais.
 *
 * <p>⚠️ <b>É `/qr/`, e este comentário já nasceu dizendo `/r/`</b> — a prova
 * pequena do que o parágrafo acima afirma. O resolvedor mudou de caminho na
 * emenda de 2026-08-14 do ADR-0006; `/r/` continua respondendo, com <b>301</b>,
 * porque adesivo colado em tambor não se reimprime. Endereço velho num
 * comentário não quebra nada — é justamente por isso que ele sobrevive, e é o
 * mesmo mecanismo pelo qual uma string copiada aqui protegeria um endereço
 * morto sem nenhum teste reclamar.
 */
const FORA_DO_SISTEMA: readonly string[] = [ROTAS.lote, ROTAS.volume];

/**
 * O endereço casa com um padrão de rota do roteador (`/…/:param/…`)?
 *
 * <p>Segmento a segmento, e `:algo` aceita um segmento qualquer — mas UM só.
 * Contagem de segmentos igual, senão `/lote/:codigo` casaria com
 * `/lote/MP-0001/qualquer-coisa`.
 */
function casaPadrao(padrao: string, endereco: string): boolean {
  const doPadrao = padrao.split("/");
  const doEndereco = endereco.split("/");
  if (doPadrao.length !== doEndereco.length) return false;

  return doPadrao.every(
    (parte, i) => (parte.startsWith(":") ? doEndereco[i] !== "" : parte === doEndereco[i]),
  );
}

/**
 * A rota cobre este endereço?
 *
 * <p>Igual, ou prefixo <b>em fronteira de segmento</b>. O `+ "/"` não é
 * paranoia: sem ele `/producao/materias` casaria com
 * `/producao/materias-primas`, e o caminho nomearia um módulo vizinho como se
 * fosse o pai da tela.
 *
 * <p>⚠️ `"/"` casa <b>só por igualdade</b>. Ele é prefixo de todo endereço do
 * sistema; aceito como prefixo, o Painel de risco viraria ancestral de cada
 * tela — "Assuntos Regulatórios › Painel de risco › Recipientes".
 */
function cobre(rota: string, endereco: string): boolean {
  if (rota === endereco) return true;
  if (rota === "/") return false;
  return endereco.startsWith(rota + "/");
}

/** Todos os candidatos do mapa, com a trilha de degraus de cada um. */
function candidatos(endereco: string): Casamento[] {
  const achados: Casamento[] = [];

  const registrar = (
    rota: string | null,
    rotulo: string,
    acima: readonly PassoDoCaminho[],
  ) => {
    if (rota === null || !cobre(rota, endereco)) return;
    achados.push({ rota, passos: [...acima, { rotulo, href: rota }] });
  };

  for (const grupo of SETORES as readonly GrupoDeNavegacao[]) {
    // O setor nomeia o nível e NÃO leva a lugar nenhum: no mapa ele é um
    // agrupador, sem rota. Escrevê-lo como link mandaria a pessoa para o vazio.
    const doSetor: PassoDoCaminho[] = [{ rotulo: grupo.rotulo, href: null }];

    for (const item of grupo.itens as readonly ItemDeNavegacao[]) {
      registrar(item.rota, item.rotulo, doSetor);

      const doModulo: PassoDoCaminho[] = [
        ...doSetor,
        { rotulo: item.rotulo, href: item.rota },
      ];
      for (const filho of item.itens ?? []) {
        registrar(filho.rota, filho.rotulo, doModulo);
      }
    }
  }

  return achados;
}

/**
 * Os degraus até esta tela, prontos para o componente `Caminho`.
 *
 * @param endereco `location.pathname` — sem consulta e sem âncora.
 * @param folha O nome da tela atual quando ele **não está na rota**: o código
 *   do lote, o do recipiente. Ausente, a tela é a própria entrada do menu.
 * @returns Os degraus da raiz até aqui, ou **lista vazia** quando não há
 *   caminho honesto a desenhar.
 */
export function caminhoDaRota(
  endereco: string,
  folha?: string | null,
): readonly PassoDoCaminho[] {
  // `/producao/materias-primas/` e `/producao/materias-primas` são o mesmo
  // lugar. A barra final chega de link escrito à mão e de redirecionamento.
  const limpo =
    endereco.length > 1 && endereco.endsWith("/") ? endereco.slice(0, -1) : endereco;

  // A recusa vem ANTES da derivação, e não depois de montar os degraus: o que
  // se está negando é que aquela tela pertença à trilha, não que a trilha tenha
  // ficado feia. Ver `FORA_DO_SISTEMA`.
  if (FORA_DO_SISTEMA.some((padrao) => casaPadrao(padrao, limpo))) return [];

  /*
   * O MAIS FUNDO vence. `/producao/recipientes` casa só consigo mesmo, mas se
   * um dia um submódulo herdar o prefixo de um módulo, é o submódulo que nomeia
   * onde a pessoa está — o módulo vira ancestral.
   */
  const melhor = candidatos(limpo).reduce<Casamento | undefined>(
    (atual, candidato) =>
      atual === undefined || candidato.rota.length > atual.rota.length ? candidato : atual,
    undefined,
  );

  if (melhor === undefined) return [];

  const passos = melhor.passos;
  const ultimo = passos[passos.length - 1]!;
  const exata = melhor.rota === limpo;

  const declarada = folha?.trim() ?? "";
  if (declarada !== "") {
    // A entrada do menu passa a ser ANCESTRAL — e ancestral leva. A folha é o
    // degrau atual, e degrau atual nunca tem endereço.
    return [...passos, { rotulo: declarada, href: null }];
  }

  /*
   * ⚠️ ENDEREÇO ABAIXO DO MENU E SEM FOLHA DECLARADA: NENHUM CAMINHO.
   *
   * Estamos em `/producao/materias-primas/<id>/resumo` e a tela ainda não disse
   * qual lote é. Devolver "Produção › Matérias-primas" marcaria MATÉRIAS-PRIMAS
   * como a página atual — e não é: é a lista, uma tela acima. O caminho estaria
   * afirmando, com `aria-current="page"`, que se está num lugar em que não se
   * está.
   *
   * Some até a tela saber o que dizer. Um caminho ausente é a pessoa lendo o
   * título; um caminho errado é a pessoa clicando confiante no degrau errado.
   */
  if (!exata) return [];

  // Casamento exato: a própria entrada do menu é a tela. Ela é o degrau atual e
  // perde o endereço — link para a página aberta é clique que não faz nada.
  return [...passos.slice(0, -1), { rotulo: ultimo.rotulo, href: null }];
}
